import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw, ExternalLink, Copy, Plus, Search, X, Activity, Wallet, Zap } from 'lucide-react';
import { getTheme } from '../App';
import { ConnectButton, useCurrentAccount, useSignAndExecuteTransaction } from '@mysten/dapp-kit';
import { Transaction } from '@mysten/sui/transactions';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AssetItem { symbol: string; amount: number; usd: number; }

interface TxEntry {
  id:          string;
  ts:          number;
  type:        'auto' | 'manual';
  borrower:    string;
  debtSymbol:  string;
  collSymbol:  string;
  repayAmount: number;
  repayUsd:    number;
  bonusUsd:    number;
  hf:          number;
  digest:      string;
  success:     boolean;
}

interface NaviOpportunity {
  address:            string;
  addressShort:       string;
  healthFactor:       number;
  totalCollateralUsd: number;
  totalDebtUsd:       number;
  topCollateral:      { symbol: string; usd: number } | null;
  topBorrow:          { symbol: string; usd: number } | null;
  borrows:            AssetItem[];
  collaterals:        AssetItem[];
  maxRepayUsd:        number;
  expectedBonusUsd:   number;
  naviUrl:            string;
}

interface Props { dark?: boolean; }

const isDev   = window.location.port === '3000' || window.location.port === '3001';
const NAVI_WS   = isDev ? `ws://${window.location.hostname}:8884`   : 'wss://ws.barbarosquant.org/navi-ws';
const NAVI_HTTP = isDev ? `http://${window.location.hostname}:8885`  : 'https://ws.barbarosquant.org/navi-http';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtUsd(v: number): string {
  if (!v) return '$0';
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toFixed(2)}`;
}

function fmtAmt(v: number): string {
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(2)}K`;
  if (v >= 1)   return v.toFixed(2);
  if (v >= 0.001) return v.toFixed(4);
  return v.toExponential(2);
}

// Hover tooltip — borç veya teminat kırılımı
function AssetTooltip({ items, label, color }: { items: AssetItem[]; label: string; color: string }) {
  const [show, setShow] = React.useState(false);
  if (!items || items.length === 0) return <span className={color}>—</span>;
  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span className={`${color} cursor-default underline decoration-dotted`}>{label}</span>
      {show && (
        <div className="absolute z-50 right-0 bottom-full mb-1 min-w-[180px] bg-[#061530] border border-blue-800/60 rounded-lg shadow-2xl p-2 text-left">
          <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-1.5">
            {items.length === 1 ? 'Varlık' : `${items.length} varlık`}
          </p>
          {items.map((item) => (
            <div key={item.symbol} className="flex items-center justify-between gap-4 py-0.5">
              <span className="text-[11px] font-bold text-white">{item.symbol}</span>
              <div className="text-right">
                <span className="text-[11px] font-mono text-blue-200">{fmtAmt(item.amount)}</span>
                <span className="text-[10px] text-blue-400 ml-1">({fmtUsd(item.usd)})</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function hfColor(hf: number, t: any): string {
  if (hf < 1.0)  return t.negative;            // zaten likide olabilir
  if (hf < 1.05) return 'text-rose-400';
  if (hf < 1.15) return 'text-orange-400';
  if (hf < 1.30) return 'text-yellow-400';
  return t.muted;
}

function hfLabel(hf: number): string {
  if (hf < 1.0)  return 'LİKİDE EDİLEBİLİR';
  if (hf < 1.05) return 'ÇOK YAKIN';
  if (hf < 1.15) return 'YAKIN';
  if (hf < 1.30) return 'İZLE';
  return 'GÜVENLİ';
}

// ─── Liquidation Modal ────────────────────────────────────────────────────────

type TxStatus = 'idle' | 'building' | 'signing' | 'success' | 'error';

const selectCls = (dark: boolean) =>
  `w-full px-3 py-2 rounded-lg text-xs font-bold border outline-none cursor-pointer appearance-none ` +
  (dark
    ? 'bg-[#061530] border-[#003087] text-white focus:border-[#4A9FE8]'
    : 'bg-white border-blue-300 text-gray-900 focus:border-blue-500');

function LiquidationModal({ op, dark, onClose }: { op: NaviOpportunity; dark: boolean; onClose: () => void }) {
  const t = getTheme(dark);

  // Build token lists — fall back to topBorrow/topCollateral if arrays empty
  const borrowList = op.borrows.length > 0
    ? op.borrows
    : op.topBorrow ? [{ symbol: op.topBorrow.symbol, amount: 0, usd: op.topBorrow.usd }] : [];
  const collList = op.collaterals.length > 0
    ? op.collaterals
    : op.topCollateral ? [{ symbol: op.topCollateral.symbol, amount: 0, usd: op.topCollateral.usd }] : [];

  const [debtSym,    setDebtSym]    = useState(borrowList[0]?.symbol ?? '');
  const [collSym,    setCollSym]    = useState(collList[0]?.symbol   ?? '');
  const [repayInput, setRepayInput] = useState('');
  const [copied,     setCopied]     = useState<string | null>(null);
  const [status,     setStatus]     = useState<TxStatus>('idle');
  const [txDigest,   setTxDigest]   = useState<string | null>(null);
  const [errMsg,     setErrMsg]     = useState<string | null>(null);

  const account                      = useCurrentAccount();
  const { mutateAsync: signAndExec } = useSignAndExecuteTransaction();

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  // Derived values from selected tokens
  const selBorrow      = borrowList.find(b => b.symbol === debtSym) ?? borrowList[0];
  const pricePerToken  = (selBorrow && selBorrow.amount > 0) ? selBorrow.usd / selBorrow.amount : 0;
  // Navi protokolü close factor'ü dahili olarak uygular (%50).
  // Biz tam borç miktarını max olarak gösteriyoruz — protokol geri kalanını iade eder.
  const totalDebt      = selBorrow ? selBorrow.amount : 0;
  const closeFactor50  = totalDebt * 0.5;  // Navi'nin tipik close factor'ü (%50)
  const maxRepay       = totalDebt;        // Kullanıcıya tam borcu göster, protokol kendi limitini uygular

  const repayNum  = repayInput !== '' ? Math.max(0, parseFloat(repayInput) || 0) : closeFactor50;
  const repayUsd  = repayNum * pricePerToken;
  const bonusUsd  = repayUsd * 0.05;
  const overMax   = totalDebt > 0 && repayNum > totalDebt * 1.001;
  // Aynı pool iki kez &mut geçilemez — Sui PTB kısıtı
  const sameAsset = debtSym !== '' && debtSym === collSym;

  const handleLiquidate = async () => {
    if (!account?.address || !debtSym || !collSym) return;
    setStatus('building');
    setErrMsg(null);
    setTxDigest(null);
    try {
      // 1) Backend PTB inşa etsin
      const res = await fetch(`${NAVI_HTTP}/build-liquidation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerAddress:    account.address,
          borrower:         op.address,
          debtSymbol:       debtSym,
          collateralSymbol: collSym,
          repayAmount:      repayNum,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || 'Backend TX inşa hatası');
      }
      const { txBytes } = await res.json();

      // 2) Wallet imzasına sun
      setStatus('signing');
      const raw = Uint8Array.from(atob(txBytes), c => c.charCodeAt(0));
      const tx  = Transaction.from(raw);
      let digest: string | null = null;
      const result = await signAndExec(
        { transaction: tx },
        {
          onSuccess: (data) => { digest = data.digest; setTxDigest(data.digest); setStatus('success'); },
          onError:   (e)    => { throw e; },
        }
      );
      if (result?.digest) { digest = result.digest; setTxDigest(result.digest); setStatus('success'); }

      // 3) Performans log'una bildir
      if (digest) {
        fetch(`${NAVI_HTTP}/report-manual-tx`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            borrower:    op.address,
            debtSymbol:  debtSym,
            collSymbol:  collSym,
            repayAmount: repayNum,
            digest,
            success:     true,
            hf:          op.healthFactor,
          }),
        }).catch(() => {}); // fire-and-forget
      }
    } catch (e: any) {
      setErrMsg(e?.message || String(e));
      setStatus('error');
    }
  };

  const statusLabel: Record<TxStatus, string> = {
    idle:     `Likide Et — ${fmtUsd(repayUsd)} ödeyip +${fmtUsd(bonusUsd)} kazan ⚡`,
    building: 'TX İnşa ediliyor...',
    signing:  'Cüzdan onayı bekleniyor...',
    success:  'Başarılı! ✓',
    error:    'Hata — Tekrar Dene',
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className={`${t.card} border ${t.cardBorder} rounded-2xl w-[640px] max-w-[95vw] max-h-[90vh] overflow-y-auto shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${t.cardBorder} flex items-center justify-between`}>
          <div>
            <p className={`text-sm font-bold ${t.text}`}>Likidasyon</p>
            <p className={`text-xs ${t.muted} mt-0.5 font-mono`}>{op.address}</p>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded ${t.btnSecondary}`}>
            <X size={14} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className={`${t.card} border ${t.cardBorder} rounded-lg p-3`}>
              <p className={`text-[11px] ${t.muted} mb-1`}>Sağlık Faktörü</p>
              <p className={`text-lg font-black ${hfColor(op.healthFactor, t)}`}>{op.healthFactor.toFixed(4)}</p>
              <p className={`text-[10px] ${hfColor(op.healthFactor, t)} font-bold mt-0.5`}>{hfLabel(op.healthFactor)}</p>
            </div>
            <div className={`${t.card} border ${t.cardBorder} rounded-lg p-3`}>
              <p className={`text-[11px] ${t.muted} mb-1`}>Toplam Borç</p>
              <p className={`text-lg font-bold ${t.text}`}>{fmtUsd(op.totalDebtUsd)}</p>
            </div>
            <div className={`${t.card} border ${t.cardBorder} rounded-lg p-3`}>
              <p className={`text-[11px] ${t.muted} mb-1`}>Toplam Teminat</p>
              <p className={`text-lg font-bold ${t.text}`}>{fmtUsd(op.totalCollateralUsd)}</p>
            </div>
          </div>

          {/* Token selection dropdowns */}
          <div className={`${t.card} border ${t.cardBorder} rounded-lg p-4 space-y-4`}>
            <p className={`text-xs font-semibold ${t.muted} uppercase tracking-wider`}>Likidasyon Parametreleri</p>

            <div className="grid grid-cols-2 gap-3">
              {/* Debt token dropdown */}
              <div className="space-y-1.5">
                <p className={`text-[11px] font-medium ${t.muted}`}>Geri ödenecek borç</p>
                <select
                  value={debtSym}
                  onChange={e => { setDebtSym(e.target.value); setRepayInput(''); }}
                  className={selectCls(dark)}
                >
                  {borrowList.map(b => (
                    <option key={b.symbol} value={b.symbol}>
                      {b.symbol}{b.amount > 0 ? ` — ${b.amount < 0.001 ? b.amount.toExponential(2) : b.amount.toFixed(4)} (${fmtUsd(b.usd)})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Collateral token dropdown */}
              <div className="space-y-1.5">
                <p className={`text-[11px] font-medium ${t.muted}`}>Alınacak teminat</p>
                <select
                  value={collSym}
                  onChange={e => setCollSym(e.target.value)}
                  className={selectCls(dark)}
                >
                  {collList.map(c => (
                    <option key={c.symbol} value={c.symbol}>
                      {c.symbol}{c.amount > 0 ? ` — ${c.amount < 0.001 ? c.amount.toExponential(2) : c.amount.toFixed(4)} (${fmtUsd(c.usd)})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Repay amount input */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <p className={`text-[11px] font-medium ${t.muted}`}>Ödenecek miktar ({debtSym || '—'})</p>
                <span className={`text-[10px] ${t.muted}`}>
                  Toplam borç: {totalDebt > 0 ? (totalDebt < 0.0001 ? totalDebt.toExponential(3) : totalDebt.toFixed(6)) : '—'} {debtSym}
                  {totalDebt > 0 && <span className="ml-1 opacity-60">(%50 = {closeFactor50 < 0.0001 ? closeFactor50.toExponential(2) : closeFactor50.toFixed(6)})</span>}
                </span>
              </div>
              <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${overMax ? 'border-red-500' : dark ? 'border-[#003087] focus-within:border-[#4A9FE8]' : 'border-blue-300 focus-within:border-blue-500'}`}>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={repayInput}
                  onChange={e => setRepayInput(e.target.value)}
                  placeholder={closeFactor50 > 0 ? closeFactor50.toFixed(6) : '0'}
                  className={`flex-1 bg-transparent text-xs font-mono outline-none placeholder:opacity-40 ${dark ? 'text-white' : 'text-gray-900'}`}
                />
                <button
                  onClick={() => setRepayInput(closeFactor50.toFixed(6))}
                  title="Navi'nin tipik close factor'ü: %50"
                  className={`text-[10px] font-bold px-2 py-0.5 rounded ${dark ? 'bg-[#003087] text-[#4A9FE8] hover:bg-[#6a30a0]' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                >
                  50%
                </button>
                <button
                  onClick={() => setRepayInput(maxRepay.toFixed(6))}
                  title="Tam borç miktarı (Navi protokolü kendi limitini uygular)"
                  className={`text-[10px] font-bold px-2 py-0.5 rounded ${dark ? 'bg-[#4A9FE8] text-[#061530] hover:bg-yellow-300' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                >
                  MAX
                </button>
              </div>
              {overMax && (
                <p className="text-[10px] text-red-400 font-medium">Toplam borcun üstünde — max {maxRepay.toFixed(6)} {debtSym}</p>
              )}
              {!overMax && totalDebt > 0 && repayNum > closeFactor50 * 1.001 && (
                <p className="text-[10px] text-yellow-400 font-medium">⚠ %50 close factor aşıldı — Navi fazlayı iade eder, yine de denenebilir</p>
              )}
              {sameAsset && (
                <p className="text-[10px] text-yellow-400 font-medium">⚠ Borç ve teminat aynı token — Sui PTB aynı pool'u iki kez &mut geçemiyor. Farklı bir teminat seçin.</p>
              )}
            </div>

            {/* Outcome display */}
            <div className={`grid grid-cols-2 gap-3 pt-1 border-t ${t.cardBorder}`}>
              <div>
                <p className={`text-[10px] ${t.muted}`}>Ödenecek (USD)</p>
                <p className={`text-sm font-bold ${t.gold} mt-0.5`}>{fmtUsd(repayUsd)}</p>
              </div>
              <div>
                <p className={`text-[10px] ${t.muted}`}>Beklenen bonus (%5)</p>
                <p className={`text-sm font-bold ${t.positive} mt-0.5`}>+{fmtUsd(bonusUsd)}</p>
              </div>
            </div>
          </div>

          {/* Wallet + liquidate */}
          <div className={`${t.card} border ${t.cardBorder} rounded-lg p-4 space-y-3`}>
            <p className={`text-xs font-semibold ${t.muted} uppercase tracking-wider`}>Cüzdan ile Likide Et</p>

            {!account ? (
              <div className="flex flex-col items-start gap-2">
                <p className={`text-xs ${t.muted}`}>Likidasyon göndermek için Sui cüzdanınızı bağlayın.</p>
                <ConnectButton connectText="Cüzdan Bağla" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`flex items-center gap-2 text-xs ${t.muted}`}>
                  <Wallet size={12} className={t.positive} />
                  <span className="font-mono">{account.address.slice(0, 10)}...{account.address.slice(-6)}</span>
                </div>

                <button
                  onClick={handleLiquidate}
                  disabled={overMax || sameAsset || repayNum <= 0 || status === 'building' || status === 'signing' || status === 'success'}
                  className={`w-full py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-opacity
                    ${status === 'success'
                      ? 'bg-green-600 text-white'
                      : status === 'error'
                      ? 'bg-red-600 text-white hover:opacity-90'
                      : overMax || sameAsset || repayNum <= 0
                      ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                      : 'bg-[#4A9FE8] text-[#061530] hover:opacity-90'}
                    ${(status === 'building' || status === 'signing') ? 'opacity-60 cursor-wait' : ''}
                  `}
                >
                  {(status === 'building' || status === 'signing') && <RefreshCw size={13} className="animate-spin" />}
                  {status === 'idle' && !overMax && !sameAsset && repayNum > 0 && <Zap size={13} />}
                  {sameAsset ? 'Aynı Token — Likide Edilemez' : repayNum <= 0 ? 'Miktar girin' : statusLabel[status]}
                </button>

                {txDigest && (
                  <a
                    href={`https://suiscan.xyz/mainnet/tx/${txDigest}`}
                    target="_blank"
                    rel="noreferrer"
                    className={`text-xs ${t.positive} flex items-center gap-1 hover:underline`}
                  >
                    <ExternalLink size={11} />
                    TX: {txDigest.slice(0, 20)}...
                  </a>
                )}
                {errMsg && <p className="text-xs text-red-400 break-all">{errMsg}</p>}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div className="flex items-center gap-2">
            <a
              href={op.naviUrl}
              target="_blank"
              rel="noreferrer"
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold ${t.btnSecondary} flex items-center justify-center gap-1.5`}
            >
              <ExternalLink size={12} />
              Navi UI
            </a>
            <a
              href={`https://suiscan.xyz/mainnet/account/${op.address}`}
              target="_blank"
              rel="noreferrer"
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold ${t.btnSecondary} flex items-center justify-center gap-1.5`}
            >
              <ExternalLink size={12} />
              Suiscan
            </a>
            <button
              onClick={() => copy(op.address, 'addr')}
              className={`px-3 py-2 rounded-lg text-xs ${t.btnSecondary} flex items-center gap-1`}
            >
              <Copy size={11} />
              {copied === 'addr' ? 'OK' : 'Adres'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function NaviLiquidator({ dark = true }: Props) {
  const t = getTheme(dark);

  const [opportunities, setOpportunities] = useState<NaviOpportunity[]>([]);
  const [scanned,        setScanned]      = useState(0);
  const [lastScanAt,     setLastScanAt]   = useState<number | null>(null);
  const [config,         setConfig]       = useState<any>({ healthFactorMax: 2.0, scanIntervalS: 20, autoEnabled: false });
  const [connected,      setConnected]    = useState(false);
  const [scanning,       setScanning]     = useState(false);
  const [search,         setSearch]       = useState('');
  const [hfMaxFilter,    setHfMaxFilter]  = useState(1.5);
  const [selected,       setSelected]     = useState<NaviOpportunity | null>(null);
  const [addAddrInput,   setAddAddrInput] = useState('');
  // bonus sıralaması: 'desc' = büyükten küçüğe, 'asc' = küçükten büyüğe, null = HF sırası
  const [bonusSort,      setBonusSort]    = useState<'asc' | 'desc' | null>(null);
  const [borrowFilter,   setBorrowFilter]   = useState('');
  const [collFilter,     setCollFilter]     = useState('');
  // TX history (manual + auto)
  const [txHistory,    setTxHistory]    = useState<TxEntry[]>([]);
  const [activeTab,    setActiveTab]    = useState<'opportunities' | 'performance'>('opportunities');

  const wsRef = useRef<WebSocket | null>(null);

  // ── WS connection ─────────────────────────────────────────────────────────
  useEffect(() => {
    let stopped = false;
    const connect = () => {
      const ws = new WebSocket(NAVI_WS);
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!stopped) setTimeout(connect, 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
      ws.onmessage = (ev) => {
        let msg: any;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === 'opportunities') {
          setOpportunities(msg.payload || []);
          setScanned(msg.scanned || 0);
          setLastScanAt(msg.ts || Date.now());
          setScanning(!!msg.partial);
        } else if (msg.type === 'opportunity_update') {
          setOpportunities(prev => {
            const i = prev.findIndex(o => o.address === msg.payload.address);
            if (i < 0) return [...prev, msg.payload].sort((a,b) => a.healthFactor - b.healthFactor);
            const next = [...prev];
            next[i] = msg.payload;
            return next.sort((a,b) => a.healthFactor - b.healthFactor);
          });
        } else if (msg.type === 'config') {
          setConfig(msg.payload || config);
        } else if (msg.type === 'tx_history') {
          setTxHistory(msg.payload || []);
        } else if (msg.type === 'tx_added') {
          setTxHistory(prev => [msg.payload, ...prev].slice(0, 500));
        }
      };
    };
    connect();
    return () => { stopped = true; try { wsRef.current?.close(); } catch {} };
  }, []);

  const sendWs = (obj: any) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  };

  // ── Token listeleri (dropdown için) ──────────────────────────────────────
  const borrowTokens = useMemo(() => {
    const s = new Set(opportunities.map(o => o.topBorrow?.symbol).filter(Boolean) as string[]);
    return Array.from(s).sort();
  }, [opportunities]);

  const collTokens = useMemo(() => {
    const s = new Set(opportunities.map(o => o.topCollateral?.symbol).filter(Boolean) as string[]);
    return Array.from(s).sort();
  }, [opportunities]);

  // ── Filtreleme & Sıralama ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const base = opportunities.filter(o => {
      if (o.healthFactor > hfMaxFilter) return false;
      if (borrowFilter && (o.topBorrow?.symbol || '') !== borrowFilter) return false;
      if (collFilter   && (o.topCollateral?.symbol || '') !== collFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!o.address.toLowerCase().includes(q)
          && !(o.topBorrow?.symbol || '').toLowerCase().includes(q)
          && !(o.topCollateral?.symbol || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
    if (bonusSort === 'desc') return [...base].sort((a, b) => b.expectedBonusUsd - a.expectedBonusUsd);
    if (bonusSort === 'asc')  return [...base].sort((a, b) => a.expectedBonusUsd - b.expectedBonusUsd);
    return base; // default: HF sırası (sunucudan geliyor)
  }, [opportunities, hfMaxFilter, search, bonusSort, borrowFilter, collFilter]);

  // ── İstatistikler ─────────────────────────────────────────────────────────
  const totalDebt        = opportunities.reduce((s, o) => s + o.totalDebtUsd, 0);
  const totalBonusPotl   = opportunities.reduce((s, o) => s + o.expectedBonusUsd, 0);
  const liquidatableNow  = opportunities.filter(o => o.healthFactor < 1.0).length;
  const closeToLiq       = opportunities.filter(o => o.healthFactor < 1.05 && o.healthFactor >= 1.0).length;

  const card = `${t.card} border ${t.cardBorder} rounded-xl`;

  // ── UI ─────────────────────────────────────────────────────────────────────
  // ── Performans hesapları ───────────────────────────────────────────────────
  const perfSuccess   = txHistory.filter(t => t.success);
  const perfFailed    = txHistory.filter(t => !t.success);
  const perfVolumeUsd = perfSuccess.reduce((s, t) => s + t.repayUsd, 0);
  const perfProfitUsd = perfSuccess.reduce((s, t) => s + t.bonusUsd, 0);
  const perfBestUsd   = perfSuccess.reduce((s, t) => Math.max(s, t.bonusUsd), 0);
  const today = new Date(); today.setHours(0,0,0,0);
  const todayTx       = perfSuccess.filter(t => t.ts >= today.getTime());
  const todayProfit   = todayTx.reduce((s, t) => s + t.bonusUsd, 0);

  return (
    <div className="p-6 space-y-5">
      {/* Header + Tab */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h2 className={`text-xl font-bold ${t.text}`}>Navi Liquidation Sniper</h2>
            <p className={`text-xs ${t.muted} mt-1`}>Sui Navi Protocol — otomatik & manuel likidasyon</p>
          </div>
          <div className={`flex rounded-lg border ${t.cardBorder} overflow-hidden`}>
            {(['opportunities', 'performance'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 text-xs font-bold transition-colors ${
                  activeTab === tab
                    ? 'bg-[#4A9FE8] text-[#061530]'
                    : `${t.btnSecondary} hover:opacity-80`
                }`}
              >
                {tab === 'opportunities' ? `📡 Fırsatlar` : `📊 Performans${txHistory.length ? ` (${txHistory.length})` : ''}`}
              </button>
            ))}
          </div>
        </div>
        <div className={`flex items-center gap-3 text-xs ${t.muted}`}>
          <div className={`flex items-center gap-1.5`}>
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-400'}`} />
            {connected ? 'Bağlı' : 'Bağlantı yok'}
          </div>
          <span>·</span>
          <span>Taranan: <b className={t.text}>{scanned}</b></span>
          {lastScanAt && (
            <>
              <span>·</span>
              <span>{new Date(lastScanAt).toLocaleTimeString('tr-TR')}</span>
            </>
          )}
        </div>
      </div>

      {activeTab === 'opportunities' && <>
      {/* Stat kartları */}
      <div className="grid grid-cols-4 gap-4">
        <div className={`${card} p-4`}>
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle size={14} className="text-rose-400" />
            <p className={`text-xs ${t.muted}`}>Likide edilebilir (HF&lt;1)</p>
          </div>
          <p className={`text-xl font-bold ${liquidatableNow > 0 ? t.negative : t.text}`}>{liquidatableNow}</p>
        </div>
        <div className={`${card} p-4`}>
          <div className="flex items-center gap-1.5 mb-1">
            <Activity size={14} className="text-orange-400" />
            <p className={`text-xs ${t.muted}`}>Çok yakın (1.00 - 1.05)</p>
          </div>
          <p className={`text-xl font-bold ${t.text}`}>{closeToLiq}</p>
        </div>
        <div className={`${card} p-4`}>
          <p className={`text-xs ${t.muted} mb-1`}>Toplam izlenen borç</p>
          <p className={`text-xl font-bold ${t.gold}`}>{fmtUsd(totalDebt)}</p>
        </div>
        <div className={`${card} p-4`}>
          <p className={`text-xs ${t.muted} mb-1`}>Toplam potansiyel bonus</p>
          <p className={`text-xl font-bold ${t.positive}`}>+{fmtUsd(totalBonusPotl)}</p>
        </div>
      </div>

      {/* Auto-liquidation paneli */}
      <div className={`${card} p-4`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap size={14} className={config.autoEnabled ? 'text-[#4A9FE8]' : t.muted} />
            <span className={`text-xs font-bold ${t.text}`}>Otomatik Likidasyon</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
              config.autoEnabled
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
            }`}>
              {config.autoEnabled ? 'AKTİF' : 'KAPALI'}
            </span>
          </div>
          {config.autoEnabled && (
            <div className={`flex items-center gap-3 text-[10px] ${t.muted}`}>
              <span>Max repay: <b className={t.gold}>${config.maxRepayUsd ?? 10}</b>/tx</span>
              <span>HF trigger: <b className="text-rose-400">&lt;{config.autoHfTrigger ?? 1.0}</b></span>
              <span className="font-mono truncate max-w-[140px]" title={config.autoSignerAddr ?? ''}>
                {config.autoSignerAddr ? `${config.autoSignerAddr.slice(0,8)}…${config.autoSignerAddr.slice(-6)}` : '—'}
              </span>
            </div>
          )}
          {!config.autoEnabled && (
            <span className={`text-[10px] ${t.muted}`}>
              Aktif etmek için .env'e <code className="opacity-70">AUTO_LIQUIDATION_ENABLED=true</code> ekleyin
            </span>
          )}
        </div>

        {/* Son TX özeti — tamamı Performans sekmesinde */}
        {txHistory.filter(t => t.type === 'auto').length === 0 ? (
          <p className={`text-[11px] ${t.muted} text-center py-2`}>
            {config.autoEnabled ? 'Henüz otomatik TX yok — HF < 1.0 pozisyon bekleniyor' : 'Bot kapalı'}
          </p>
        ) : (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {txHistory.filter(t => t.type === 'auto').slice(0, 8).map((tx) => (
              <div key={tx.id} className={`flex items-center gap-2 text-[10px] px-2 py-1 rounded ${dark ? 'bg-black/30' : 'bg-gray-50'}`}>
                <span className={tx.success ? 'text-green-400' : 'text-red-400'}>{tx.success ? '✓' : '✗'}</span>
                <span className={`font-mono ${t.muted}`}>{tx.borrower?.slice(0, 8)}…</span>
                <span className={`font-bold ${t.text}`}>{tx.debtSymbol}→{tx.collSymbol}</span>
                <span className={t.gold}>${tx.repayUsd?.toFixed(2)}</span>
                <span className={t.positive}>+${tx.bonusUsd?.toFixed(3)}</span>
                <span className={`${t.muted} ml-auto`}>HF {tx.hf?.toFixed(4)}</span>
                {tx.digest && tx.digest !== '?' && tx.success && (
                  <a href={`https://suiscan.xyz/mainnet/tx/${tx.digest}`} target="_blank" rel="noreferrer" className="text-blue-400" onClick={e => e.stopPropagation()}>
                    <ExternalLink size={9} />
                  </a>
                )}
                <span className={`${t.muted} text-[9px]`}>{new Date(tx.ts).toLocaleTimeString('tr-TR')}</span>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => setActiveTab('performance')} className={`mt-2 text-[10px] ${t.muted} hover:${t.text} underline w-full text-center`}>
          Tüm geçmişi gör →
        </button>
      </div>

      {/* Filter bar */}
      <div className={`${card} p-3 flex items-center gap-3 flex-wrap`}>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${dark ? 'bg-[#0a2560]/60 border-[#003087]' : 'bg-white border-blue-300'}`}>
          <Search size={13} className={t.muted} />
          <input
            placeholder="Adres veya sembol..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`bg-transparent text-xs outline-none w-48 ${t.text}`}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className={`text-xs ${t.muted}`}>HF eşiği:</span>
          {[1.0, 1.05, 1.15, 1.5, 2.0].map(v => (
            <button
              key={v}
              onClick={() => setHfMaxFilter(v)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                hfMaxFilter === v ? 'bg-[#4A9FE8] text-[#061530] font-bold' : t.btnSecondary
              }`}
            >
              &lt; {v.toFixed(2)}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Manuel adres ekle */}
        <div className={`flex items-center gap-2 px-2 py-1 rounded-lg border ${dark ? 'bg-[#0a2560]/60 border-[#003087]' : 'bg-white border-blue-300'}`}>
          <input
            placeholder="0x... cüzdan ekle"
            value={addAddrInput}
            onChange={(e) => setAddAddrInput(e.target.value)}
            className={`bg-transparent text-xs outline-none w-44 ${t.text}`}
          />
          <button
            onClick={() => {
              if (addAddrInput.trim()) {
                sendWs({ type: 'add_address', address: addAddrInput.trim() });
                setAddAddrInput('');
              }
            }}
            className={`p-1 rounded ${t.btnSecondary}`}
            title="Cüzdanı tarama listesine ekle"
          >
            <Plus size={12} />
          </button>
        </div>

        <button
          onClick={() => sendWs({ type: 'ping' })}
          className={`px-2.5 py-1.5 rounded-lg text-xs ${t.btnSecondary} flex items-center gap-1`}
          title="Yenile"
        >
          <RefreshCw size={12} />
        </button>
      </div>

      {/* Tablo */}
      <div className={`${card} overflow-hidden`}>
        <div className={`px-4 py-3 border-b ${t.cardBorder} flex items-center justify-between`}>
          <p className={`text-xs font-semibold ${t.muted}`}>
            Likidasyon Fırsatları — HF&lt;{hfMaxFilter.toFixed(2)}
            {scanning && <span className="ml-2 text-yellow-400 animate-pulse">⟳ taranıyor...</span>}
          </p>
          <span className={`text-xs ${t.muted}`}>{filtered.length} pozisyon</span>
        </div>

        {filtered.length === 0 ? (
          <div className={`p-10 text-center ${t.muted} text-xs`}>
            {connected
              ? (opportunities.length === 0
                  ? 'Cüzdan listesi taranıyor... ilk sonuçlar 30-60 saniye içinde gelir.'
                  : 'Bu eşiğin altında pozisyon yok. Filtreyi yumuşatın.')
              : 'Backend bağlantısı kurulamadı (port 8884).'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`${t.tableHeader} border-b`}>
                  <th className="px-3 py-2.5 text-left">HF</th>
                  <th className="px-3 py-2.5 text-left">Durum</th>
                  <th className="px-3 py-2.5 text-left">Cüzdan</th>
                  <th className="px-3 py-2.5 text-right">Toplam Borç</th>
                  <th className="px-3 py-2.5 text-right">Toplam Teminat</th>
                  <th className="px-3 py-2.5 text-left">
                    <div className="flex items-center gap-1">
                      <span>Borç Token</span>
                      <select
                        value={borrowFilter}
                        onChange={e => setBorrowFilter(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className={`ml-1 text-[10px] font-normal rounded px-1 py-0.5 border outline-none cursor-pointer appearance-none max-w-[80px]
                          ${dark ? 'bg-[#061530] border-[#003087] text-white' : 'bg-white border-blue-300 text-gray-900'}
                          ${borrowFilter ? (dark ? 'border-[#4A9FE8] text-[#4A9FE8]' : 'border-blue-600 text-blue-700') : ''}`}
                      >
                        <option value="">Hepsi</option>
                        {borrowTokens.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </th>
                  <th className="px-3 py-2.5 text-left">
                    <div className="flex items-center gap-1">
                      <span>Teminat Token</span>
                      <select
                        value={collFilter}
                        onChange={e => setCollFilter(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className={`ml-1 text-[10px] font-normal rounded px-1 py-0.5 border outline-none cursor-pointer appearance-none max-w-[80px]
                          ${dark ? 'bg-[#061530] border-[#003087] text-white' : 'bg-white border-blue-300 text-gray-900'}
                          ${collFilter ? (dark ? 'border-[#4A9FE8] text-[#4A9FE8]' : 'border-blue-600 text-blue-700') : ''}`}
                      >
                        <option value="">Hepsi</option>
                        {collTokens.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </th>
                  <th className="px-3 py-2.5 text-right">Max Repay</th>
                  <th
                    className="px-3 py-2.5 text-right cursor-pointer select-none hover:opacity-70"
                    onClick={() => setBonusSort(s => s === 'desc' ? 'asc' : s === 'asc' ? null : 'desc')}
                    title="Bonusa göre sırala"
                  >
                    Beklenen Bonus
                    <span className="ml-1 text-[9px]">
                      {bonusSort === 'desc' ? '▼' : bonusSort === 'asc' ? '▲' : '⇅'}
                    </span>
                  </th>
                  <th className="px-3 py-2.5 text-right">Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(op => (
                  <tr
                    key={op.address}
                    className={`border-b ${t.tableRow} cursor-pointer`}
                    onClick={() => setSelected(op)}
                  >
                    <td className={`px-3 py-2 font-mono font-black ${hfColor(op.healthFactor, t)}`}>
                      {op.healthFactor.toFixed(4)}
                    </td>
                    <td className={`px-3 py-2 font-bold ${hfColor(op.healthFactor, t)} text-[10px]`}>
                      {hfLabel(op.healthFactor)}
                    </td>
                    <td className={`px-3 py-2 font-mono ${t.text}`}>{op.addressShort}</td>
                    <td className={`px-3 py-2 text-right font-mono ${t.text}`}>
                      <AssetTooltip
                        items={op.borrows}
                        label={fmtUsd(op.totalDebtUsd)}
                        color={t.text}
                      />
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${t.muted}`}>
                      <AssetTooltip
                        items={op.collaterals}
                        label={fmtUsd(op.totalCollateralUsd)}
                        color={t.muted}
                      />
                    </td>
                    <td className={`px-3 py-2 ${t.text}`}>{op.topBorrow?.symbol || '—'}</td>
                    <td className={`px-3 py-2 ${t.text}`}>{op.topCollateral?.symbol || '—'}</td>
                    <td className={`px-3 py-2 text-right font-mono ${t.gold}`}>{fmtUsd(op.maxRepayUsd)}</td>
                    <td className={`px-3 py-2 text-right font-mono font-bold ${t.positive}`}>+{fmtUsd(op.expectedBonusUsd)}</td>
                    <td className={`px-3 py-2 text-right`}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelected(op); }}
                        className="px-2.5 py-1 rounded text-[11px] font-bold bg-[#4A9FE8] text-[#061530] hover:opacity-90"
                      >
                        Likide Et
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bilgi notu */}
      <div className={`${card} p-4 text-xs ${t.muted} space-y-1`}>
        <p>· Tarama her <b>{config.scanIntervalS}s</b>'de bir Sui RPC üzerinden Navi event'lerini okur ve borçlu cüzdanları günceller.</p>
        <p>· Navi close factor: <b>%50</b> — tek çağrıda borcun en fazla bu kadarını ödeyebilirsiniz. Bonus: <b>%5</b> (varlığa göre değişir).</p>
      </div>
      </>}

      {/* ── PERFORMANS SEKMESİ ─────────────────────────────────────────────── */}
      {activeTab === 'performance' && (
        <div className="space-y-5">
          {/* Özet kartları */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`${card} p-4`}>
              <p className={`text-[11px] ${t.muted} mb-1`}>Toplam İşlem</p>
              <p className={`text-2xl font-black ${t.text}`}>{txHistory.length}</p>
              <p className={`text-[10px] mt-1`}>
                <span className="text-green-400">{perfSuccess.length} başarılı</span>
                {perfFailed.length > 0 && <span className="text-red-400 ml-2">{perfFailed.length} başarısız</span>}
              </p>
            </div>
            <div className={`${card} p-4`}>
              <p className={`text-[11px] ${t.muted} mb-1`}>Toplam Hacim</p>
              <p className={`text-2xl font-black ${t.gold}`}>{fmtUsd(perfVolumeUsd)}</p>
              <p className={`text-[10px] ${t.muted} mt-1`}>repay edilen toplam</p>
            </div>
            <div className={`${card} p-4`}>
              <p className={`text-[11px] ${t.muted} mb-1`}>Toplam Kâr</p>
              <p className={`text-2xl font-black ${t.positive}`}>+{fmtUsd(perfProfitUsd)}</p>
              <p className={`text-[10px] ${t.muted} mt-1`}>tahmini %5 bonus</p>
            </div>
            <div className={`${card} p-4`}>
              <p className={`text-[11px] ${t.muted} mb-1`}>Bugün</p>
              <p className={`text-2xl font-black ${t.positive}`}>+{fmtUsd(todayProfit)}</p>
              <p className={`text-[10px] ${t.muted} mt-1`}>{todayTx.length} işlem · En iyi: +{fmtUsd(perfBestUsd)}</p>
            </div>
          </div>

          {/* TX Tablosu */}
          <div className={`${card} overflow-hidden`}>
            <div className={`px-4 py-3 border-b ${t.cardBorder} flex items-center justify-between`}>
              <span className={`text-xs font-bold ${t.text}`}>İşlem Geçmişi</span>
              <span className={`text-[10px] ${t.muted}`}>{txHistory.length} kayıt</span>
            </div>
            {txHistory.length === 0 ? (
              <div className="py-12 text-center">
                <p className={`text-sm ${t.muted}`}>Henüz likidasyon işlemi yok</p>
                <p className={`text-xs ${t.muted} mt-1 opacity-60`}>Manuel veya otomatik likidasyon yapıldığında burada görünecek</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={`border-b ${t.cardBorder} ${t.muted} text-[10px] uppercase tracking-wider`}>
                      <th className="px-3 py-2 text-left">Zaman</th>
                      <th className="px-3 py-2 text-left">Tür</th>
                      <th className="px-3 py-2 text-left">Borrower</th>
                      <th className="px-3 py-2 text-left">Çift</th>
                      <th className="px-3 py-2 text-right">Repay</th>
                      <th className="px-3 py-2 text-right">Bonus</th>
                      <th className="px-3 py-2 text-right">HF</th>
                      <th className="px-3 py-2 text-center">Durum</th>
                      <th className="px-3 py-2 text-center">TX</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txHistory.map((tx) => (
                      <tr key={tx.id} className={`border-b ${t.cardBorder} hover:bg-white/5 transition-colors`}>
                        <td className={`px-3 py-2 font-mono ${t.muted} whitespace-nowrap`}>
                          <div>{new Date(tx.ts).toLocaleDateString('tr-TR', { day:'2-digit', month:'2-digit' })}</div>
                          <div className="text-[9px]">{new Date(tx.ts).toLocaleTimeString('tr-TR')}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                            tx.type === 'auto'
                              ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {tx.type === 'auto' ? '⚡ Auto' : '👤 Manuel'}
                          </span>
                        </td>
                        <td className={`px-3 py-2 font-mono ${t.muted} text-[10px]`}>
                          {tx.borrower?.slice(0,8)}…{tx.borrower?.slice(-4)}
                        </td>
                        <td className={`px-3 py-2 font-bold ${t.text}`}>
                          {tx.debtSymbol}<span className={t.muted}>→</span>{tx.collSymbol}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono ${t.gold}`}>
                          {fmtUsd(tx.repayUsd)}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono font-bold ${tx.success ? t.positive : t.muted}`}>
                          {tx.success ? `+${fmtUsd(tx.bonusUsd)}` : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono ${tx.hf < 1 ? 'text-rose-400' : 'text-orange-400'}`}>
                          {tx.hf?.toFixed(4) ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {tx.success
                            ? <span className="text-green-400 font-bold">✓</span>
                            : <span className="text-red-400 font-bold">✗</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {tx.digest && tx.digest !== '?' ? (
                            <a
                              href={`https://suiscan.xyz/mainnet/tx/${tx.digest}`}
                              target="_blank" rel="noreferrer"
                              className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-0.5"
                            >
                              <ExternalLink size={10} />
                            </a>
                          ) : <span className={`${t.muted} text-[9px]`}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Alt özet */}
          {perfSuccess.length > 0 && (
            <div className={`${card} p-4 grid grid-cols-3 gap-4 text-center`}>
              <div>
                <p className={`text-[10px] ${t.muted}`}>Başarı Oranı</p>
                <p className={`text-lg font-black ${t.positive}`}>
                  {txHistory.length > 0 ? ((perfSuccess.length / txHistory.length) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div>
                <p className={`text-[10px] ${t.muted}`}>Ortalama Bonus/TX</p>
                <p className={`text-lg font-black ${t.gold}`}>
                  +{fmtUsd(perfSuccess.length > 0 ? perfProfitUsd / perfSuccess.length : 0)}
                </p>
              </div>
              <div>
                <p className={`text-[10px] ${t.muted}`}>En Yüksek Tek TX</p>
                <p className={`text-lg font-black ${t.positive}`}>+{fmtUsd(perfBestUsd)}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {selected && <LiquidationModal op={selected} dark={dark} onClose={() => setSelected(null)} />}
    </div>
  );
}
